import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '@/services/api';
import { queryKeys } from '@/lib/queryKeys';
import {
  EMPTY_PIPELINE_STATUS_COUNTS,
  sumPipelineStatusCounts,
  type PipelineStatusCounts,
} from '@/lib/dashboardApplicationBuckets';
import type { Period } from '@/hooks/useDashboardMetrics';

export function useAgencyPipelineStatusCounts(
  companyIds: string[],
  period: Period,
  enabled = true,
): {
  counts: PipelineStatusCounts;
  periodAppCount: number;
  loading: boolean;
  ready: boolean;
} {
  const queries = useQueries({
    queries: companyIds.map((companyId) => ({
      queryKey: queryKeys.pipelineStatusCounts(companyId, period),
      queryFn: () => api.dashboard.pipelineStatusCounts(companyId, period),
      enabled: enabled && companyIds.length > 0,
      staleTime: 60_000,
    })),
  });

  const loading = queries.some((q) => q.isPending || q.isFetching);
  const ready = queries.some((q) => q.data?.counts != null);

  const counts = useMemo(() => {
    const parts = queries
      .map((q) => q.data?.counts)
      .filter((c): c is PipelineStatusCounts => c != null);
    if (parts.length === 0) return EMPTY_PIPELINE_STATUS_COUNTS();
    return sumPipelineStatusCounts(...parts);
  }, [queries]);

  const periodAppCount = useMemo(
    () => queries.reduce((sum, q) => sum + (q.data?.periodAppCount ?? 0), 0),
    [queries],
  );

  return { counts, periodAppCount, loading, ready };
}
